import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { User, Calendar, FileText, Receipt, Phone, Mail, MapPin, ArrowLeft, Clock, AlertCircle, Plus, X, Lock, Pencil } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import BodyChart from '../components/BodyChart';

function PatientDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [noteForm, setNoteForm] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    painScale: '',
    treatmentProvided: '',
    romMeasurements: '',
    bodyChart: []
  });
  const [invoiceForm, setInvoiceForm] = useState({
    items: [{ description: '', quantity: 1, unitPrice: '' }],
    vatRate: 0,
    notes: ''
  });
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    appointmentDate: '',
    startTime: '',
    endTime: '',
    type: ''
  });
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [editPatientForm, setEditPatientForm] = useState({
    dateOfBirth: '',
    phone: '',
    email: '',
    address: ''
  });
  const [editingNoteId, setEditingNoteId] = useState(null);

  useEffect(() => {
    fetchPatient();
    fetchInvoices();
  }, [id]);

  useEffect(() => {
    if (patient && searchParams.get('edit') === 'true') {
      handleOpenEditPatient();
    }
  }, [patient, searchParams]);

  const fetchInvoices = async () => {
    try {
      const data = await api.get(`/invoices/patient/${id}`);
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const handleEditAppointment = (apt) => {
    setEditingAppointment(apt);
    setAppointmentForm({
      appointmentDate: apt.appointmentDate,
      startTime: apt.startTime.substring(0, 5),
      endTime: apt.endTime.substring(0, 5),
      type: apt.type
    });
    setShowAppointmentModal(true);
  };

  const handleSaveAppointment = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/appointments/${editingAppointment.id}/reschedule`, {
        appointmentDate: appointmentForm.appointmentDate,
        startTime: appointmentForm.startTime,
        endTime: appointmentForm.endTime
      });
      toast.success('Appointment updated');
      setShowAppointmentModal(false);
      fetchPatient();
    } catch (error) {
      toast.error(error.error || 'Failed to update appointment');
    }
    setSaving(false);
  };

  const handleOpenEditPatient = () => {
    setEditPatientForm({
      dateOfBirth: patient.dateOfBirth || '',
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || ''
    });
    setShowEditPatientModal(true);
  };

  const handleSavePatient = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/patients/${id}`, editPatientForm);
      toast.success('Patient details updated');
      setShowEditPatientModal(false);
      fetchPatient();
    } catch (error) {
      toast.error(error.error || 'Failed to update patient');
    }
    setSaving(false);
  };

  const fetchPatient = async () => {
    try {
      const data = await api.get(`/patients/${id}`);
      if (data.patient) {
        setPatient(data.patient);
      } else {
        toast.error('Patient data not found');
      }
    } catch (error) {
      toast.error(error.error || 'Failed to load patient details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteForm({
      subjective: note.subjective || '',
      objective: note.objective || '',
      assessment: note.assessment || '',
      plan: note.plan || '',
      painScale: note.painScale ? String(note.painScale) : '',
      treatmentProvided: note.treatmentProvided || '',
      romMeasurements: note.romMeasurements || '',
      bodyChart: note.bodyChart || []
    });
    setShowNoteModal(true);
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingNoteId) {
        await api.put(`/notes/${editingNoteId}`, {
          ...noteForm,
          painScale: noteForm.painScale ? parseInt(noteForm.painScale) : null
        });
        toast.success('Clinical note updated');
      } else {
        await api.post('/notes', {
          patientId: id,
          ...noteForm,
          painScale: noteForm.painScale ? parseInt(noteForm.painScale) : null
        });
        toast.success('Clinical note saved');
      }
      setShowNoteModal(false);
      setEditingNoteId(null);
      setNoteForm({
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        painScale: '',
        treatmentProvided: '',
        romMeasurements: '',
        bodyChart: []
      });
      fetchPatient();
    } catch (error) {
      toast.error(error.error || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, { description: '', quantity: 1, unitPrice: '' }]
    });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems.length ? newItems : [{ description: '', quantity: 1, unitPrice: '' }] });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceForm({ ...invoiceForm, items: newItems });
  };

  const calculateInvoiceTotals = () => {
    const subtotal = invoiceForm.items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
    }, 0);
    const vat = subtotal * (parseFloat(invoiceForm.vatRate) || 0) / 100;
    return { subtotal, vat, total: subtotal + vat };
  };

  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { subtotal, vat, total } = calculateInvoiceTotals();
      const items = invoiceForm.items.map(item => ({
        ...item,
        total: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)
      }));
      
      await api.post('/invoices', {
        patientId: id,
        items,
        subtotal,
        vat,
        vatRate: parseFloat(invoiceForm.vatRate) || 0,
        total,
        notes: invoiceForm.notes
      });
      
      toast.success('Invoice created');
      setShowInvoiceModal(false);
      setInvoiceForm({
        items: [{ description: '', quantity: 1, unitPrice: '' }],
        vatRate: 0,
        notes: ''
      });
      fetchInvoices();
    } catch (error) {
      toast.error(error.error || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  const markInvoicePaid = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}/pay`, { paymentMethod: 'card' });
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const sendInvoice = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`);
      toast.success('Invoice sent to patient');
      fetchInvoices();
    } catch (error) {
      toast.error(error.error || 'Failed to send invoice');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <AlertCircle size={48} style={{ color: 'var(--danger)', marginBottom: '1rem' }} />
        <h2>Patient not found</h2>
        <Link to="/patients" className="btn btn-primary" style={{ marginTop: '1rem' }}>
          Back to Patients
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'notes', label: 'Clinical Notes', icon: FileText },
    { id: 'invoices', label: 'Invoices', icon: Receipt }
  ];

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return format(new Date(date), 'dd MMM yyyy');
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: { bg: '#dbeafe', color: '#1d4ed8' },
      confirmed: { bg: '#dcfce7', color: '#16a34a' },
      completed: { bg: '#f3f4f6', color: '#4b5563' },
      cancelled: { bg: '#fee2e2', color: '#dc2626' },
      no_show: { bg: '#fef3c7', color: '#d97706' },
      draft: { bg: '#e0e7ff', color: '#4338ca' },
      sent: { bg: '#dbeafe', color: '#1d4ed8' },
      paid: { bg: '#dcfce7', color: '#16a34a' },
      overdue: { bg: '#fee2e2', color: '#dc2626' }
    };
    const style = colors[status] || colors.scheduled;
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        textTransform: 'capitalize'
      }}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div>
      <Link to="/patients" style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '0.5rem',
        color: 'var(--text-light)',
        marginBottom: '1rem',
        textDecoration: 'none'
      }}>
        <ArrowLeft size={16} /> Back to Patients
      </Link>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 600,
            flexShrink: 0
          }}>
            {patient.firstName?.[0]}{patient.lastName?.[0]}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ margin: 0, marginBottom: '0.5rem' }}>
              {patient.firstName} {patient.lastName}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', color: 'var(--text-light)', fontSize: '0.875rem' }}>
              {patient.dateOfBirth && (
                <span>DOB: {formatDate(patient.dateOfBirth)}</span>
              )}
              {patient.phone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Phone size={14} /> {patient.phone}
                </span>
              )}
              {patient.email && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Mail size={14} /> {patient.email}
                </span>
              )}
            </div>
            {patient.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-light)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                <MapPin size={14} /> {patient.address}
              </div>
            )}
          </div>
          <button 
            onClick={handleOpenEditPatient}
            style={{ alignSelf: 'flex-start', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', color: 'var(--text-light)' }}
            title="Edit patient details"
          >
            <Pencil size={18} />
          </button>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        gap: '0.5rem', 
        marginBottom: '1.5rem',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '0.5rem',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              border: 'none',
              background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text)',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Medical History</h3>
          <p style={{ color: 'var(--text-light)' }}>
            {patient.medicalHistory || 'No medical history recorded'}
          </p>
          
          {patient.allergies && (
            <>
              <h4>Allergies</h4>
              <p style={{ color: 'var(--danger)' }}>{patient.allergies}</p>
            </>
          )}

          {patient.medications && (
            <>
              <h4>Current Medications</h4>
              <p>{patient.medications}</p>
            </>
          )}

          {patient.gpName && (
            <>
              <h4>GP Details</h4>
              <p>{patient.gpName} {patient.gpSurgery && `- ${patient.gpSurgery}`}</p>
            </>
          )}

          {patient.insuranceProvider && (
            <>
              <h4>Insurance</h4>
              <p>{patient.insuranceProvider} {patient.insurancePolicyNumber && `(${patient.insurancePolicyNumber})`}</p>
            </>
          )}
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Appointments</h3>
          {patient.appointments?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {patient.appointments.map(apt => (
                <div key={apt.id} style={{
                  padding: '1rem',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  position: 'relative'
                }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {formatDate(apt.appointmentDate)}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Clock size={14} />
                      {apt.startTime} - {apt.endTime}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginTop: '0.25rem' }}>
                      {apt.type?.replace('_', ' ')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getStatusBadge(apt.status)}
                    <button
                      onClick={() => handleEditAppointment(apt)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '0.25rem' }}
                      title="Edit appointment"
                    >
                      <Pencil size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-light)' }}>No appointments found</p>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Clinical Notes (SOAP)</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowNoteModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Plus size={16} /> New Note
            </button>
          </div>
          {patient.clinicalNotes?.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {patient.clinicalNotes.map(note => (
                <div key={note.id} style={{
                  padding: '1rem',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius)',
                  borderLeft: note.isLocked ? '3px solid var(--success)' : '3px solid var(--primary)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontWeight: 600 }}>{formatDate(note.createdAt)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {note.isLocked ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Lock size={12} /> Locked
                        </span>
                      ) : (
                        <button
                          onClick={() => handleEditNote(note)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem',
                            color: 'var(--text-light)',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Edit note"
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  {note.subjective && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>S - Subjective:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.subjective}</p>
                    </div>
                  )}
                  {note.objective && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>O - Objective:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.objective}</p>
                    </div>
                  )}
                  {note.assessment && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>A - Assessment:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.assessment}</p>
                    </div>
                  )}
                  {note.plan && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>P - Plan:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.plan}</p>
                    </div>
                  )}
                  {note.treatmentProvided && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Treatment:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.treatmentProvided}</p>
                    </div>
                  )}
                  {note.romMeasurements && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>ROM:</strong>
                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{note.romMeasurements}</p>
                    </div>
                  )}
                  {note.painScale && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      Pain Scale: {note.painScale}/10
                    </div>
                  )}
                  {note.bodyChart && note.bodyChart.length > 0 && (
                    <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'white', borderRadius: '4px' }}>
                      <strong style={{ fontSize: '0.8rem' }}>Body Chart Markers:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                        {note.bodyChart.map((marker, idx) => (
                          <span key={idx} style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.15rem 0.4rem', 
                            background: '#dcfce7', 
                            borderRadius: '3px',
                            color: '#166534'
                          }}>
                            {idx + 1}. {marker.label || 'Unlabeled'} ({marker.view})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-light)' }}>No clinical notes recorded. Click "New Note" to add one.</p>
          )}
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Invoices</h3>
            <button className="btn btn-primary" onClick={() => setShowInvoiceModal(true)}>
              <Plus size={16} style={{ marginRight: '0.25rem' }} />
              New Invoice
            </button>
          </div>
          {invoices.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id}>
                      <td>
                        <Link to={`/invoices/${inv.id}`} style={{ color: '#ec4899', fontWeight: 600 }}>
                          {inv.invoiceNumber}
                        </Link>
                      </td>
                      <td>{formatDate(inv.invoiceDate)}</td>
                      <td>£{parseFloat(inv.total || 0).toFixed(2)}</td>
                      <td>{getStatusBadge(inv.status)}</td>
                      <td style={{ display: 'flex', gap: '0.5rem' }}>
                        <Link 
                          to={`/invoices/${inv.id}`}
                          className="btn btn-sm" 
                          style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                        >
                          View
                        </Link>
                        {inv.status === 'draft' && (
                          <button 
                            className="btn btn-sm btn-primary" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => sendInvoice(inv.id)}
                          >
                            Send
                          </button>
                        )}
                        {inv.status !== 'paid' && (
                          <button 
                            className="btn btn-sm" 
                            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => markInvoicePaid(inv.id)}
                          >
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-light)' }}>No invoices found. Click "New Invoice" to create one.</p>
          )}
        </div>
      )}

      {showNoteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius)',
            width: '100%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--border)'
            }}>
              <h2 style={{ margin: 0 }}>{editingNoteId ? 'Edit' : 'New'} Clinical Note (SOAP)</h2>
              <button
                onClick={() => { setShowNoteModal(false); setEditingNoteId(null); setNoteForm({ subjective: '', objective: '', assessment: '', plan: '', painScale: '', treatmentProvided: '', romMeasurements: '', bodyChart: [] }); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleNoteSubmit} style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">S - Subjective (Patient's complaints)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="What the patient reports: symptoms, pain description, history..."
                  value={noteForm.subjective}
                  onChange={(e) => setNoteForm({ ...noteForm, subjective: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">O - Objective (Clinical findings)</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Your observations: posture, palpation, tests, measurements..."
                  value={noteForm.objective}
                  onChange={(e) => setNoteForm({ ...noteForm, objective: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">A - Assessment (Diagnosis)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Your clinical impression and diagnosis..."
                  value={noteForm.assessment}
                  onChange={(e) => setNoteForm({ ...noteForm, assessment: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">P - Plan (Treatment plan)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Treatment plan, exercises, follow-up..."
                  value={noteForm.plan}
                  onChange={(e) => setNoteForm({ ...noteForm, plan: e.target.value })}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label className="form-label">Pain Scale (0-10)</label>
                  <input
                    type="number"
                    className="form-input"
                    min="0"
                    max="10"
                    placeholder="0-10"
                    value={noteForm.painScale}
                    onChange={(e) => setNoteForm({ ...noteForm, painScale: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">ROM Measurements</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g., Flexion 90°, Extension 10°"
                    value={noteForm.romMeasurements}
                    onChange={(e) => setNoteForm({ ...noteForm, romMeasurements: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Treatment Provided</label>
                <textarea
                  className="form-input"
                  rows={2}
                  placeholder="Manual therapy, exercises given, modalities used..."
                  value={noteForm.treatmentProvided}
                  onChange={(e) => setNoteForm({ ...noteForm, treatmentProvided: e.target.value })}
                />
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="form-label">Body Chart</label>
                <BodyChart 
                  markers={noteForm.bodyChart} 
                  onChange={(markers) => setNoteForm({ ...noteForm, bodyChart: markers })}
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowNoteModal(false); setEditingNoteId(null); setNoteForm({ subjective: '', objective: '', assessment: '', plan: '', painScale: '', treatmentProvided: '', romMeasurements: '', bodyChart: [] }); }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAppointmentModal && editingAppointment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }} onClick={() => setShowAppointmentModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius)',
            width: '100%',
            maxWidth: 400,
            padding: '1.5rem'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0 }}>Edit Appointment</h2>
              <button onClick={() => setShowAppointmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveAppointment}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={appointmentForm.appointmentDate}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, appointmentDate: e.target.value })}
                  required
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label className="form-label">Start Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={appointmentForm.startTime}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, startTime: e.target.value })}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="form-label">End Time</label>
                  <input
                    type="time"
                    className="form-input"
                    value={appointmentForm.endTime}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, endTime: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAppointmentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInvoiceModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: 'var(--radius)',
            width: '100%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid var(--border)'
            }}>
              <h2 style={{ margin: 0 }}>New Invoice</h2>
              <button
                onClick={() => setShowInvoiceModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleInvoiceSubmit} style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Line Items</label>
                {invoiceForm.items.map((item, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Description"
                      style={{ flex: 2 }}
                      value={item.description}
                      onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                      required
                    />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Qty"
                      style={{ width: 70 }}
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)}
                      required
                    />
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Price"
                      style={{ width: 90 }}
                      step="0.01"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => updateInvoiceItem(index, 'unitPrice', e.target.value)}
                      required
                    />
                    {invoiceForm.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInvoiceItem(index)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary" onClick={addInvoiceItem} style={{ marginTop: '0.5rem' }}>
                  <Plus size={14} style={{ marginRight: '0.25rem' }} />
                  Add Item
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label className="form-label">VAT Rate (%)</label>
                  <input
                    type="number"
                    className="form-input"
                    min="0"
                    max="100"
                    value={invoiceForm.vatRate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, vatRate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Optional notes"
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })}
                  />
                </div>
              </div>
              <div style={{ background: 'var(--background)', padding: '1rem', borderRadius: 'var(--radius)', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span>Subtotal:</span>
                  <span>£{calculateInvoiceTotals().subtotal.toFixed(2)}</span>
                </div>
                {parseFloat(invoiceForm.vatRate) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>VAT ({invoiceForm.vatRate}%):</span>
                    <span>£{calculateInvoiceTotals().vat.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', borderTop: '1px solid var(--border)', paddingTop: '0.5rem' }}>
                  <span>Total:</span>
                  <span>£{calculateInvoiceTotals().total.toFixed(2)}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowInvoiceModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditPatientModal && (
        <div className="modal-overlay" onClick={() => setShowEditPatientModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Edit Patient Details</h2>
              <button className="btn btn-secondary" onClick={() => setShowEditPatientModal(false)} style={{ padding: '0.5rem' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSavePatient}>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input
                  type="date"
                  className="form-input"
                  value={editPatientForm.dateOfBirth}
                  onChange={(e) => setEditPatientForm({ ...editPatientForm, dateOfBirth: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={editPatientForm.phone}
                  onChange={(e) => setEditPatientForm({ ...editPatientForm, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={editPatientForm.email}
                  onChange={(e) => setEditPatientForm({ ...editPatientForm, email: e.target.value })}
                  placeholder="Email address"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={editPatientForm.address}
                  onChange={(e) => setEditPatientForm({ ...editPatientForm, address: e.target.value })}
                  placeholder="Full address"
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditPatientModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientDetail;
